// Las categorías de la tienda: el árbol y el selector de la ficha —
// ADR-2026-09-07-categorias-de-tienda.
//
// Son dos pantallas de la MISMA lista, y por eso viven en el mismo archivo: el
// gestor deja armar el árbol y el selector deja atarle un producto. Si se
// separaran, la indentación, el plegado y el orden habría que escribirlos dos
// veces y se irían despegando.
import { boton } from './botones';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, FolderPlus, Loader2, Pencil, Plus, Tag, Trash2, X } from 'lucide-react';
import { SheetLateral } from '../../components/SheetLateral';
import PopoverAnclado from './PopoverAnclado';
import {
  aplanarArbolConGuias,
  caminoDe,
  destinosPosibles,
  hermanaRepetida,
  ramaDe,
} from './arbolDeCategorias';
import type { CategoriaDeTienda } from './lanzamientosApi';

/** Cuánto se corre cada nivel. Suficiente para leerse, poco para no quedarse sin ancho. */
const SANGRIA = 20;
/** Margen izquierdo de la primera columna: el texto no pega contra el borde del hover. */
const INICIO_ARBOL = 8;

/**
 * Las líneas guía de una fila del árbol (├ └ │) — ADR-2026-10-22-el-arbol-de-categorias-con-lineas.
 * Van absolutas dentro de la fila (que tiene que ser `relative` y sin margen vertical, para que
 * las líneas de una fila empalmen con las de la siguiente). Cada nivel ocupa `ancho` px a partir
 * de `inicio`; la línea pasa a `eje` px del comienzo de esa columna, debajo del comienzo del texto
 * (o de la casilla) de la madre, y el codo llega hasta poco antes del de la fila. La flechita de
 * plegar va a la DERECHA del nombre (ADR-2026-10-22-el-arbol-de-categorias-con-lineas-2).
 */
export function LineasDeLaFila({
  nivel,
  guias,
  esUltima,
  ancho = SANGRIA,
  inicio = INICIO_ARBOL,
  eje = 6,
}: {
  nivel: number;
  guias: boolean[];
  esUltima: boolean;
  ancho?: number;
  inicio?: number;
  eje?: number;
}) {
  if (nivel === 0) return null;
  const x = (columna: number) => inicio + columna * ancho + eje;
  const linea = 'pointer-events-none absolute bg-black/[0.13]';
  return (
    <>
      {/* Las verticales de las ancestras que todavía tienen hermanas abajo. */}
      {guias.slice(1).map((sigue, j) =>
        sigue ? <span key={j} aria-hidden className={`${linea} inset-y-0 w-px`} style={{ left: x(j) }} /> : null,
      )}
      {/* El codo: baja desde la madre y dobla hacia la fila. */}
      <span
        aria-hidden
        className={`${linea} top-0 w-px`}
        style={{ left: x(nivel - 1), height: esUltima ? '50%' : '100%' }}
      />
      <span
        aria-hidden
        className={`${linea} top-1/2 h-px`}
        style={{ left: x(nivel - 1), width: Math.max(6, ancho - eje - 4) }}
      />
    </>
  );
}

/** La flechita de plegar, a la derecha del nombre: › plegada, ⌄ abierta. */
export function BotonDePlegar({ plegada, onClick }: { plegada: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // Dentro de una fila que se elige al tocarla, plegar no la elige.
        e.stopPropagation();
        onClick();
      }}
      aria-label={plegada ? 'Desplegar' : 'Plegar'}
      aria-expanded={!plegada}
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-neutral-400 transition-colors hover:bg-black/[0.05] hover:text-brand-black"
    >
      <ChevronRight className={`w-3.5 h-3.5 transition-transform ${plegada ? '' : 'rotate-90'}`} />
    </button>
  );
}

// ── Alta ───────────────────────────────────────────────────────────────────

/**
 * El campo para sumar una categoría. Vive abierto o cerrado según `madre`:
 * `undefined` = cerrado, `null` = va al primer nivel, un id = va adentro de esa.
 *
 * El aviso de nombre repetido se calcula mientras se escribe y no al mandar: la
 * base lo rechaza igual, pero enterarse después de apretar es enterarse tarde.
 */
function AltaDeCategoria({
  categorias,
  madre,
  onCancelar,
  onCrear,
}: {
  categorias: readonly CategoriaDeTienda[];
  madre: string | null;
  onCancelar: () => void;
  onCrear: (nombre: string, madre: string | null) => Promise<void>;
}) {
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const repetida = hermanaRepetida(categorias, madre, nombre);
  const puede = nombre.trim() !== '' && !repetida && !guardando;

  const mandar = async () => {
    if (!puede) return;
    setGuardando(true);
    await onCrear(nombre.trim(), madre);
    setGuardando(false);
  };

  return (
    <div className="py-2" style={{ paddingLeft: madre ? SANGRIA : 0 }}>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          value={nombre}
          placeholder={madre ? 'Nombre de la subcategoría' : 'Nombre de la categoría'}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void mandar();
            if (e.key === 'Escape') onCancelar();
          }}
          className={`min-w-0 flex-1 rounded-xl border py-2 px-3 text-sm text-brand-black outline-none ${
            repetida ? 'border-amber-400' : 'border-neutral-200 focus:border-neutral-400'
          }`}
        />
        <button
          type="button"
          disabled={!puede}
          onClick={() => void mandar()}
          className={boton('primario', { extra: 'shrink-0 py-2 px-4' })}
        >
          {guardando && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          Crear
        </button>
        <button
          type="button"
          onClick={onCancelar}
          aria-label="Cancelar"
          className="shrink-0 rounded-lg p-2 text-neutral-400 hover:bg-neutral-100"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {repetida && (
        <p className="mt-1 text-[11px] text-amber-700">
          Ya tenés una categoría con ese nombre en el mismo lugar.
        </p>
      )}
    </div>
  );
}

// ── El gestor ──────────────────────────────────────────────────────────────

/** Menú de una categoría: renombrar, mover de madre, borrar. */
function AccionesDeCategoria({
  categoria,
  categorias,
  onRenombrar,
  onMover,
  onBorrar,
}: {
  categoria: CategoriaDeTienda;
  categorias: readonly CategoriaDeTienda[];
  onRenombrar: () => void;
  onMover: (parentId: string | null) => void;
  onBorrar: () => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [moviendo, setMoviendo] = useState(false);
  const botonRef = useRef<HTMLButtonElement | null>(null);

  // Ella misma y su descendencia no son destinos: meterla adentro de una hija
  // suya cierra un círculo y el árbol se queda sin raíz.
  const destinos = useMemo(
    () => destinosPosibles(categorias, categoria.id),
    [categorias, categoria.id],
  );

  return (
    <>
      <button
        ref={botonRef}
        type="button"
        onClick={() => {
          setMoviendo(false);
          setAbierto((v) => !v);
        }}
        aria-label={`Opciones de ${categoria.nombre}`}
        className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-opacity [@media(hover:hover)]:opacity-0 hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      <PopoverAnclado
        abierto={abierto}
        anclaRef={botonRef}
        onCerrar={() => setAbierto(false)}
        alineacion="derecha"
        ancho={232}
        capa={230}
      >
        {moviendo ? (
          <>
            <p className="border-b border-neutral-100 py-2 px-3 text-[11px] text-neutral-500">
              Mover adentro de…
            </p>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onMover(null);
              }}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Sin madre (primer nivel)
            </button>
            {destinos.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  setAbierto(false);
                  onMover(d.id);
                }}
                className="block w-full truncate py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
              >
                {caminoDe(categorias, d.id)}
              </button>
            ))}
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onRenombrar();
              }}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Renombrar
            </button>
            <button
              type="button"
              onClick={() => setMoviendo(true)}
              className="block w-full py-2.5 px-3 text-left text-sm text-brand-black hover:bg-neutral-50"
            >
              Mover a otra categoría
            </button>
            <button
              type="button"
              onClick={() => {
                setAbierto(false);
                onBorrar();
              }}
              className="block w-full border-t border-neutral-100 py-2.5 px-3 text-left text-sm text-red-600 hover:bg-red-50"
            >
              Borrar
            </button>
          </>
        )}
      </PopoverAnclado>
    </>
  );
}

/**
 * El panel para armar el árbol. Se abre desde el lanzamiento y desde la ficha.
 *
 * Es un `SheetLateral` y no una pantalla aparte porque armar categorías es algo
 * que se hace EN MEDIO de completar una ficha: mandar al usuario a otra ruta le
 * haría perder de vista el producto que estaba cargando.
 */
/**
 * El árbol: la lista indentada, plegable, con el botón para crear una
 * subcategoría adentro de cualquier fila y el de crear una de primer nivel
 * abajo.
 *
 * Está acá y no adentro del panel porque lo usan dos pantallas: el panel donde
 * se editan las categorías y el que elige a cuál van los productos
 * seleccionados. Que se vean igual no es coincidencia — son la misma lista, y
 * dos versiones parecidas se despegan a la primera corrección.
 *
 * Lo único que cambia entre las dos es qué se hace con una fila: el panel deja
 * renombrarla, el otro la elige. Eso entra por `renderNombre` y `acciones`.
 */
export function ArbolDeCategorias({
  categorias,
  onCrear,
  renderNombre,
  acciones,
  deshabilitado,
  vacio,
  plegarAlTocarNombre = false,
}: {
  categorias: CategoriaDeTienda[];
  onCrear: (nombre: string, madre: string | null) => Promise<void>;
  /** Qué se dibuja donde va el nombre. Por defecto, el nombre (el de primer nivel, en negrita). */
  renderNombre?: (c: CategoriaDeTienda, nivel: number) => React.ReactNode;
  /** Botones al final de la fila. */
  acciones?: (c: CategoriaDeTienda) => React.ReactNode;
  deshabilitado?: boolean;
  /** Qué mostrar cuando no hay ninguna. */
  vacio?: React.ReactNode;
  /**
   * Tocar el nombre de una categoría con subcategorías la abre o la cierra, como la flechita. Para
   * el panel de edición, donde el nombre no hace otra cosa; no para el de categorizar en lote, donde
   * tocarlo elige (ADR-2026-10-22-tocar-el-nombre-pliega-la-categoria).
   */
  plegarAlTocarNombre?: boolean;
}) {
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set());
  const [creandoEn, setCreandoEn] = useState<string | null | undefined>(undefined);

  const filas = useMemo(() => aplanarArbolConGuias(categorias, plegadas), [categorias, plegadas]);

  const plegar = (id: string) =>
    setPlegadas((p) => {
      const s = new Set(p);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const crear = async (nombre: string, madre: string | null) => {
    await onCrear(nombre, madre);
    setCreandoEn(undefined);
  };

  return (
    <div>
      {filas.length === 0 && creandoEn === undefined && vacio}

      {/* Cada categoría de primer nivel abre un grupo, separado del anterior por una línea; adentro,
          las subcategorías cuelgan con líneas guía (ADR-2026-10-22-el-arbol-de-categorias-con-lineas). */}
      <ul>
        {filas.map(({ categoria: c, nivel, tieneHijas, esUltima, guias }, i) => (
          <li key={c.id} className={nivel === 0 && i > 0 ? 'mt-2 border-t border-black/[0.06] pt-2' : undefined}>
            <div
              className="group relative flex items-center gap-1 rounded-lg py-1.5 pr-1 hover:bg-neutral-50"
              style={{ paddingLeft: INICIO_ARBOL + nivel * SANGRIA }}
            >
              <LineasDeLaFila nivel={nivel} guias={guias} esUltima={esUltima} />
              <div
                className={`flex min-w-0 flex-1 items-center gap-0.5 ${
                  plegarAlTocarNombre && tieneHijas ? 'cursor-pointer select-none' : ''
                }`}
                onClick={
                  plegarAlTocarNombre && tieneHijas
                    ? (e) => {
                        // Escribiendo el nombre nuevo (renombrar) no se pliega.
                        if ((e.target as HTMLElement).closest('input, button')) return;
                        plegar(c.id);
                      }
                    : undefined
                }
              >
                {renderNombre ? (
                  renderNombre(c, nivel)
                ) : (
                  <span
                    className={`min-w-0 truncate text-sm text-brand-black ${nivel === 0 ? 'font-semibold' : ''}`}
                  >
                    {c.nombre}
                  </span>
                )}
                {tieneHijas && (
                  <BotonDePlegar plegada={plegadas.has(c.id)} onClick={() => plegar(c.id)} />
                )}
              </div>

              <button
                type="button"
                disabled={deshabilitado}
                onClick={() => setCreandoEn(c.id)}
                // Invisible hasta el hover SÓLO con mouse: con `hoverOnlyWhenSupported`, en el
                // teléfono el `group-hover` no existe y quedaban ocultas para siempre
                // (ADR-2026-10-22-las-acciones-de-categoria-se-ven-en-el-telefono).
                aria-label={`Agregar una subcategoría en ${c.nombre}`}
                title="Agregar una subcategoría"
                className="shrink-0 rounded-lg p-1.5 text-neutral-400 transition-opacity [@media(hover:hover)]:opacity-0 hover:bg-neutral-100 hover:text-brand-black focus:opacity-100 group-hover:opacity-100 disabled:opacity-0"
              >
                <FolderPlus className="w-3.5 h-3.5" />
              </button>
              {acciones?.(c)}
            </div>

            {creandoEn === c.id && (
              <div className="relative" style={{ paddingLeft: INICIO_ARBOL + (nivel + 1) * SANGRIA }}>
                <LineasDeLaFila
                  nivel={nivel + 1}
                  guias={[...guias, !esUltima]}
                  esUltima={!(tieneHijas && !plegadas.has(c.id))}
                />
                <AltaDeCategoria
                  categorias={categorias}
                  madre={c.id}
                  onCancelar={() => setCreandoEn(undefined)}
                  onCrear={crear}
                />
              </div>
            )}
          </li>
        ))}
      </ul>

      {creandoEn === null ? (
        <AltaDeCategoria
          categorias={categorias}
          madre={null}
          onCancelar={() => setCreandoEn(undefined)}
          onCrear={crear}
        />
      ) : (
        <button
          type="button"
          disabled={deshabilitado}
          onClick={() => setCreandoEn(null)}
          className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-neutral-300 py-2.5 text-xs text-neutral-600 hover:bg-neutral-50 disabled:opacity-40"
        >
          <Plus className="w-3.5 h-3.5" /> Nueva categoría
        </button>
      )}
    </div>
  );
}

export function PanelDeCategorias({
  abierto,
  categorias,
  cargando,
  onCerrar,
  onCrear,
  onRenombrar,
  onMover,
  onBorrar,
  titulo = 'Categorías de la tienda',
  descripcion = 'Son las de tu tienda, no las de este lanzamiento: se comparten entre todos. Una subcategoría es una categoría adentro de otra.',
}: {
  abierto: boolean;
  categorias: CategoriaDeTienda[];
  cargando: boolean;
  /** Desde Mi marca el panel no habla de "este lanzamiento". Sin la prop, el texto de siempre. */
  titulo?: string;
  descripcion?: string;
  onCerrar: () => void;
  onCrear: (nombre: string, madre: string | null) => Promise<{ error: string | null }>;
  onRenombrar: (id: string, nombre: string) => Promise<string | null>;
  onMover: (id: string, parentId: string | null) => Promise<string | null>;
  onBorrar: (id: string) => Promise<string | null>;
}) {
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [nombreEditado, setNombreEditado] = useState('');
  const [porBorrar, setPorBorrar] = useState<CategoriaDeTienda | null>(null);
  const [error, setError] = useState<string | null>(null);

  const conError = async (accion: () => Promise<string | null>) => {
    setError(await accion());
  };

  // Cuántas se lleva puestas el borrado: es la diferencia entre "borrás una" y
  // "borrás tu catálogo entero", y hay que decirlo ANTES.
  const seLleva = porBorrar ? ramaDe(categorias, porBorrar.id).size : 0;

  return (
    <SheetLateral open={abierto} onClose={onCerrar} titulo={titulo}>
      <div className="p-4">
        <p className="text-xs leading-relaxed text-neutral-500">{descripcion}</p>

        {error && (
          <p className="mt-3 rounded-xl bg-red-50 py-2 px-3 text-xs text-red-700">{error}</p>
        )}

        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="mt-3">
            <ArbolDeCategorias
              categorias={categorias}
              plegarAlTocarNombre
              onCrear={async (nombre, madre) => {
                setError((await onCrear(nombre, madre)).error);
              }}
              vacio={
                <div className="rounded-2xl border border-dashed border-neutral-300 py-10 px-4 text-center">
                  <Tag className="mx-auto w-6 h-6 text-neutral-300" />
                  <p className="mt-3 text-sm font-medium text-brand-black">
                    Todavía no tenés ninguna
                  </p>
                  <p className="mx-auto mt-1 max-w-xs text-xs text-neutral-500">
                    Las categorías son la navegación de tu tienda: “Remeras”, y adentro
                    “Oversize”.
                  </p>
                </div>
              }
              renderNombre={(c, nivel) =>
                renombrando === c.id ? (
                  <input
                    autoFocus
                    value={nombreEditado}
                    onChange={(e) => setNombreEditado(e.target.value)}
                    onBlur={() => setRenombrando(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') setRenombrando(null);
                      if (e.key === 'Enter') {
                        const v = nombreEditado.trim();
                        setRenombrando(null);
                        if (v && v !== c.nombre) void conError(() => onRenombrar(c.id, v));
                      }
                    }}
                    className="min-w-0 flex-1 rounded-lg border border-neutral-300 py-1 px-2 text-sm text-brand-black outline-none"
                  />
                ) : (
                  <span
                    className={`min-w-0 truncate text-sm text-brand-black ${nivel === 0 ? 'font-semibold' : ''}`}
                  >
                    {c.nombre}
                  </span>
                )
              }
              acciones={(c) => (
                <AccionesDeCategoria
                  categoria={c}
                  categorias={categorias}
                  onRenombrar={() => {
                    setNombreEditado(c.nombre);
                    setRenombrando(c.id);
                  }}
                  onMover={(parentId) => void conError(() => onMover(c.id, parentId))}
                  onBorrar={() => setPorBorrar(c)}
                />
              )}
            />
          </div>
        )}
      </div>

      {/* Confirmar el borrado dentro del panel: decir cuántas se lleva es el
          dato que hace falta para decidir. */}
      {porBorrar &&
        createPortal(
          <div className="fixed inset-0 z-[240] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4">
          <div className="w-full max-w-sm rounded-t-3xl bg-white p-6 sm:rounded-3xl">
            <div className="flex items-start gap-3">
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-red-50 text-red-600">
                <Trash2 className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <h2 className="text-base font-semibold text-brand-black">
                  ¿Borrar “{porBorrar.nombre}”?
                </h2>
                <p className="mt-1 text-sm leading-relaxed text-neutral-500">
                  {seLleva > 1
                    ? `Se borra con sus ${seLleva - 1} subcategoría${seLleva - 1 === 1 ? '' : 's'}. Los productos que las tenían quedan sin esa categoría.`
                    : 'Los productos que la tenían quedan sin esa categoría.'}
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setPorBorrar(null)}
                className={boton('neutro', { extra: 'flex-1' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  const id = porBorrar.id;
                  setPorBorrar(null);
                  void conError(() => onBorrar(id));
                }}
                className="flex-1 rounded-xl bg-red-600 py-2.5 px-5 text-sm font-medium text-white hover:bg-red-700"
              >
                Borrar
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </SheetLateral>
  );
}

// ── El selector de la ficha ────────────────────────────────────────────────

/**
 * Qué categorías lleva este producto. El mismo árbol, con casillas.
 *
 * Marcar una NO marca a su madre. En Tiendanube un producto pertenece a las
 * categorías que le pongas y a ninguna más: si estar en "Oversize" pusiera
 * también "Remeras", el usuario no podría decir "solo en la subcategoría", que
 * es una cosa que se quiere decir.
 */
export function SelectorDeCategorias({
  categorias,
  elegidas,
  onCambiar,
  onAbrirPanel,
}: {
  categorias: CategoriaDeTienda[];
  elegidas: readonly string[];
  onCambiar: (id: string, puesta: boolean) => void;
  onAbrirPanel: () => void;
}) {
  const [plegadas, setPlegadas] = useState<Set<string>>(new Set());
  const filas = useMemo(() => aplanarArbolConGuias(categorias, plegadas), [categorias, plegadas]);
  const puestas = useMemo(() => new Set(elegidas), [elegidas]);

  const plegar = useCallback(
    (id: string) =>
      setPlegadas((p) => {
        const s = new Set(p);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        return s;
      }),
    [],
  );

  if (categorias.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-neutral-300 py-6 px-4 text-center">
        <p className="text-xs text-neutral-500">
          Todavía no armaste las categorías de tu tienda.
        </p>
        <button
          type="button"
          onClick={onAbrirPanel}
          className="mt-2 text-xs font-medium text-brand-black underline"
        >
          Armarlas ahora
        </button>
      </div>
    );
  }

  return (
    <div>
      <ul className="max-h-72 overflow-auto">
        {filas.map(({ categoria: c, nivel, tieneHijas, esUltima, guias }, i) => (
          <li key={c.id} className={nivel === 0 && i > 0 ? 'mt-1.5 border-t border-black/[0.06] pt-1.5' : undefined}>
            <div
              className="relative flex items-center gap-0.5 rounded-lg py-1 pr-1 hover:bg-neutral-50"
              style={{ paddingLeft: INICIO_ARBOL + nivel * SANGRIA }}
            >
              {/* `eje` 8: la línea baja desde el centro de la casilla de la madre. */}
              <LineasDeLaFila nivel={nivel} guias={guias} esUltima={esUltima} eje={8} />
              <label className="flex min-w-0 cursor-pointer items-center gap-2 py-1">
                <input
                  type="checkbox"
                  checked={puestas.has(c.id)}
                  onChange={(e) => onCambiar(c.id, e.target.checked)}
                  className="h-4 w-4 shrink-0 rounded border-neutral-300 accent-brand-primary"
                />
                <span className={`min-w-0 truncate text-sm text-brand-black ${nivel === 0 ? 'font-semibold' : ''}`}>
                  {c.nombre}
                </span>
              </label>
              {tieneHijas && <BotonDePlegar plegada={plegadas.has(c.id)} onClick={() => plegar(c.id)} />}
            </div>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={onAbrirPanel}
        className="mt-2 flex items-center gap-1.5 text-xs text-neutral-500 hover:text-brand-black"
      >
        <Plus className="w-3.5 h-3.5" /> Crear o editar categorías
      </button>
    </div>
  );
}
